import { useCallback, useEffect, useState } from 'react';
import { IServerColumnConfig } from '@/components/table/column-config';
import {
	deleteAnnouncement,
	fetchAnnouncementsTableConfig,
	fetchAnnouncementsList,
	fetchSingleAnnouncement,
	patchAnnouncement,
	postAnnouncement,
	postArchiveAnnouncement,
	postPublishAnnouncement
} from './actions';
import {
	AnnouncementsListReturnType,
	IAnnouncementCreatePayload,
	IAnnouncementListItem,
	IAnnouncementsListQuery,
	IAnnouncementUpdatePayload
} from './types';

// Listă server-side: pagina construiește query-ul din filtre și apelează getAnnouncements(query, page, items).
export function useAnnouncementsList(
	itemsSet = 10,
	withTableConfig = false
): AnnouncementsListReturnType {
	const [data, setData] = useState<IAnnouncementListItem[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	// Configurația de coloane (api/announcements/table-config) — cerută doar de pagina
	// de listă (withTableConfig); la eroare rămâne null și tabelul cade silențios pe
	// coloanele definite local.
	const [tableConfig, setTableConfig] = useState<IServerColumnConfig[] | null>(null);
	const [loadingTableConfig, setLoadingTableConfig] = useState<boolean>(
		Boolean(withTableConfig)
	);
	const [page, setPage] = useState<number>(0);
	const [items, setItems] = useState<number>(itemsSet);
	const [totalPages, setTotalPages] = useState<number>(1);
	const [totalItems, setTotalItems] = useState<number>(0);
	const [loadingCreate, setLoadingCreate] = useState<boolean>(false);
	const [loadingDelete, setLoadingDelete] = useState<boolean>(false);
	const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
	const [loadingStatus, setLoadingStatus] = useState<boolean>(false);

	const handleResponse = (pagesParam: number, itemsParam: number): void => {
		setTotalPages(pagesParam);
		setTotalItems(itemsParam);
	};

	useEffect(() => {
		if (!withTableConfig) return undefined;
		let active = true;
		setLoadingTableConfig(true);
		fetchAnnouncementsTableConfig()
			.then((columns) => {
				if (active) setTableConfig(columns.length ? columns : null);
			})
			.catch(() => {
				// Fallback silențios: fără config BE, tabelul folosește coloanele locale.
				if (active) setTableConfig(null);
			})
			.finally(() => {
				if (active) setLoadingTableConfig(false);
			});
		return () => {
			active = false;
		};
	}, [withTableConfig]);

	const getAnnouncements = useCallback(
		async (query: IAnnouncementsListQuery, pageParam: number, itemsParam: number) => {
			try {
				setLoading(true);
				const response = await fetchAnnouncementsList(
					query,
					pageParam,
					itemsParam,
					handleResponse
				);
				setData(response);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoading(false);
			}
		},
		[]
	);

	// GET api/announcements/:id — detaliul unui anunț.
	const getAnnouncement = useCallback(async (announcementId: number | string) => {
		try {
			setLoading(true);
			return await fetchSingleAnnouncement(announcementId);
		} catch {
			return null;
		} finally {
			setLoading(false);
		}
	}, []);

	const createAnnouncement = useCallback(
		async (payload: IAnnouncementCreatePayload, onSuccess?: () => void) => {
			try {
				setLoadingCreate(true);
				const response = await postAnnouncement(payload);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingCreate(false);
			}
		},
		[]
	);

	const updateAnnouncement = useCallback(
		async (
			announcementId: number | string,
			payload: IAnnouncementUpdatePayload,
			onSuccess?: (updated: IAnnouncementListItem) => void
		) => {
			try {
				setLoadingUpdate(true);
				const updated = await patchAnnouncement(announcementId, payload);
				onSuccess && onSuccess(updated);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingUpdate(false);
			}
		},
		[]
	);

	const publishAnnouncement = useCallback(
		async (
			announcementId: number | string,
			onSuccess?: (updated: IAnnouncementListItem) => void
		) => {
			try {
				setLoadingStatus(true);
				const updated = await postPublishAnnouncement(announcementId);
				onSuccess && onSuccess(updated);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingStatus(false);
			}
		},
		[]
	);

	const archiveAnnouncement = useCallback(
		async (
			announcementId: number | string,
			onSuccess?: (updated: IAnnouncementListItem) => void
		) => {
			try {
				setLoadingStatus(true);
				const updated = await postArchiveAnnouncement(announcementId);
				onSuccess && onSuccess(updated);
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingStatus(false);
			}
		},
		[]
	);

	const removeAnnouncement = useCallback(
		async (announcementId: number | string, onSuccess?: () => void) => {
			try {
				setLoadingDelete(true);
				const response = await deleteAnnouncement(announcementId);
				if (response === 200) {
					onSuccess && onSuccess();
				}
			} catch {
				// eroarea ajunge în toast + jurnal prin pipeline-ul global de erori
			} finally {
				setLoadingDelete(false);
			}
		},
		[]
	);

	return {
		data,
		loading,
		tableConfig,
		loadingTableConfig,
		loadingCreate,
		loadingDelete,
		loadingUpdate,
		loadingStatus,
		getAnnouncements,
		getAnnouncement,
		createAnnouncement,
		updateAnnouncement,
		publishAnnouncement,
		archiveAnnouncement,
		removeAnnouncement,
		page,
		items,
		setPage,
		setItems,
		totalPages,
		totalItems
	};
}
